import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Card from "../../../components/ui/card";
import { CreditCard, Edit3, Save, X, Building2, User, AlertCircle } from "lucide-react";
import Button from "../../../components/ui/Button";
import { Switch } from "../../../components/ui/switch";
import { Input } from "../../../components/ui/input";
import Label from "../../../components/ui/label";
import { UpdateUserProfile } from "../../../redux/Actions/AuthAction";
import { GlobalService } from "../../../modules";
import CountryDropdown from "../../../components/ui/CountryDropdown";
import { updateUserCompanies, getUserCompanies, useAppDispatch, createUserCompany, validateVATNumber } from "../../../redux";
import { isCompleteBillingAddress, isCompleteCompanyBillingAddress } from "../../../utils/billingUtils";

interface Country {
    id: string;
    name: string;
    code: string;  // Country code (e.g., "DE", "US", "FR")
}

interface BillingAddress {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;         // Country name for display
    countryId?: string;      // Country ID (reference to countries collection)
    countryCode?: string;    // Country code (initial) for tax
    companyName?: string;
    taxId?: string;
    vatNumber?: string;      // VAT number for company billing
    idType?: string;         // ID type selection (taxId, registrationNumber, vatNumber, vatNumberVies)
    isDefault?: boolean;     // Whether this address should be used as default
    isVatValid?: boolean;    // Whether the VAT number is validated
}

const BillingComponent = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const appDispatch = useAppDispatch();
    const { user } = useSelector((state: any) => state.auth);
    const { company } = useSelector((state: any) => state.client);
    const { countries } = useSelector((state: any) => state.admin || { countries: [] });
    
    const [settings, setSettings] = useState<any>({
        billing: {
            defaultPayment: {
                type: "credit_card",
                last4: "4242",
                expiryDate: "12/24"
            },
            autoRecharge: {
                enabled: true,
                threshold: 100,
                amount: 500
            }
        },
    });

    const [billingInformation, setBillingInformation] = useState<BillingAddress>({
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
        countryId: "",
        countryCode: "",
    });

    const [companyBillingInformation, setCompanyBillingInformation] = useState<BillingAddress>({
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
        countryId: "",
        countryCode: "",
        companyName: "",
        taxId: "",
        vatNumber: "",
        idType: "vatNumber", // Default to VAT Number
        isVatValid: false, // Default to false
    });

    const [selectedBillingCountry, setSelectedBillingCountry] = useState<Country | null>(null);
    const [selectedCompanyCountry, setSelectedCompanyCountry] = useState<Country | null>(null);

    const [isEditingBilling, setIsEditingBilling] = useState(false);
    const [isEditingCompanyBilling, setIsEditingCompanyBilling] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [vatValidationError, setVatValidationError] = useState<string>("");
    const [defaultAddressType, setDefaultAddressType] = useState<"personal" | "company">("personal");
    const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
    const [isValidatingVAT, setIsValidatingVAT] = useState<boolean>(false);
    const [vatValidationStatus, setVatValidationStatus] = useState<"valid" | "invalid" | "pending" | null>(null);

    // ID type configuration mapping
    const idTypeToFieldMap: Record<string, { label: string; placeholder: string; field: string }> = {
        taxId: {
            label: t("billingSettings.taxId"),
            placeholder: t("billingSettings.taxIdPlaceholder"),
            field: "vatNumber",
        },
        registrationNumber: {
            label: t("billingSettings.businessNumber"),
            placeholder: t("billingSettings.businessNumberPlaceholder"),
            field: "vatNumber",
        },
        vatNumber: {
            label: t("billingSettings.vatNumberEU"),
            placeholder: t("billingSettings.vatNumberEUPlaceholder"),
            field: "vatNumber",
        },
        vatNumberVies: {
            label: t("billingSettings.vatNumberVIES"),
            placeholder: t("billingSettings.vatNumberVIESPlaceholder"),
            field: "vatNumber",
        },
    };

    // Get current ID configuration
    const idConfig = idTypeToFieldMap[companyBillingInformation.idType || "vatNumber"] || idTypeToFieldMap.vatNumber;

    // Load existing billing information from user data and company information
    useEffect(() => {
        if (user) {
            if (user.billingInformation) {
                setBillingInformation({
                    street: user.billingInformation.street || "",
                    city: user.billingInformation.city || "",
                    state: user.billingInformation.state || "",
                    zipCode: user.billingInformation.zipCode || "",
                    country: user.billingInformation.country || "",
                    countryId: user.billingInformation.countryId || "",
                    countryCode: user.billingInformation.countryCode || "",
                    isDefault: user.billingInformation.isDefault || false,
                });
                
                // Set selected country for dropdown
                if (user.billingInformation.countryId && user.billingInformation.country && user.billingInformation.countryCode) {
                    setSelectedBillingCountry({
                        id: user.billingInformation.countryId,
                        name: user.billingInformation.country,
                        code: user.billingInformation.countryCode,
                    });
                }
            }
            
            // Load company billing information from user.companyBillingInformation or populate from company data
            if (user.companyBillingInformation) {
                setCompanyBillingInformation({
                    street: user.companyBillingInformation.street || "",
                    city: user.companyBillingInformation.city || "",
                    state: user.companyBillingInformation.state || "",
                    zipCode: user.companyBillingInformation.zipCode || "",
                    country: user.companyBillingInformation.country || "",
                    countryId: user.companyBillingInformation.countryId || "",
                    countryCode: user.companyBillingInformation.countryCode || "",
                    companyName: user.companyBillingInformation.companyName || "",
                    taxId: user.companyBillingInformation.taxId || "",
                    vatNumber: user.companyBillingInformation.vatNumber || "",
                    idType: user.companyBillingInformation.idType || "vatNumber",
                    isDefault: user.companyBillingInformation.isDefault || false,
                    isVatValid: user.companyBillingInformation.isVatValid || false,
                });
                
                // Set selected country for dropdown
                if (user.companyBillingInformation.countryId && user.companyBillingInformation.country && user.companyBillingInformation.countryCode) {
                    setSelectedCompanyCountry({
                        id: user.companyBillingInformation.countryId,
                        name: user.companyBillingInformation.country,
                        code: user.companyBillingInformation.countryCode,
                    });
                }
            } else if (user.company || company) {
                // Populate company billing information from company data if no specific billing info exists
                const companyData = user.company || company;
                
                // Determine ID type based on existing company data
                let idType = "vatNumber"; // Default
                if (companyData.vatNumber) {
                    if (companyData.isVatValid) {
                        idType = "vatNumberVies";
                    } else {
                        idType = "vatNumber";
                    }
                } else if (companyData.taxId) {
                    idType = "taxId";
                } else if (companyData.registrationNumber) {
                    idType = "registrationNumber";
                }
                
                setCompanyBillingInformation(prev => ({
                    ...prev,
                    companyName: companyData.name || "",
                    vatNumber: companyData.vatNumber || "",
                    taxId: companyData.taxId || "",
                    street: companyData.address || "",
                    city: companyData.city || "",
                    state: companyData.state || "",
                    zipCode: companyData.postalCode || "",
                    country: companyData.countryName || companyData.country || "", // Use country name if available
                    countryId: companyData.countryId || companyData.country || "", // Properly handle country ID
                    countryCode: companyData.countryCode || "",
                    idType: idType,
                    isVatValid: companyData.isVatValid || false,
                }));

                // Set selected country for dropdown if company has country data
                if ((companyData.countryId || companyData.country) && !selectedCompanyCountry) {
                    // Try to find the country from the countries list first
                    const countryFromList = countries?.find((c: any) => 
                        c.id === (companyData.countryId || companyData.country)
                    );
                    
                    if (countryFromList) {
                        setSelectedCompanyCountry({
                            id: countryFromList.id,
                            name: countryFromList.name,
                            code: countryFromList.code,
                        });
                    } else {
                        // Fallback to available data if not found in list
                        setSelectedCompanyCountry({
                            id: companyData.countryId || companyData.country || "",
                            name: companyData.countryName || companyData.country || "",
                            code: companyData.countryCode || "",
                        });
                    }
                }
            }
        }

        // Determine default address type based on existing data
        // Priority: explicit isDefault flag, then prefer personal if both exist, then any complete address
        if (user.billingInformation?.isDefault && isCompleteBillingAddress(user.billingInformation)) {
            setDefaultAddressType("personal");
        } else if (user.companyBillingInformation?.isDefault && isCompleteCompanyBillingAddress(user.companyBillingInformation)) {
            setDefaultAddressType("company");
        } else if (isCompleteBillingAddress(user.billingInformation)) {
            // Fallback: if no explicit default, prefer personal if it's complete
            setDefaultAddressType("personal");
        } else if (isCompleteCompanyBillingAddress(user.companyBillingInformation)) {
            // Otherwise use company if it's complete
            setDefaultAddressType("company");
        }
    }, [user, company]);

    // Load company data if not already loaded
    useEffect(() => {
        if (user && !company) {
            appDispatch(getUserCompanies({ id: user.id }));
        }
    }, [user, company, appDispatch]);

    // Set selected country when countries data and personal billing info are available
    useEffect(() => {
        if (billingInformation.countryId && countries && countries.length > 0 && !selectedBillingCountry) {
            const foundCountry = countries.find((country: any) => country.id === billingInformation.countryId);
            if (foundCountry) {
                setSelectedBillingCountry({
                    id: foundCountry.id,
                    name: foundCountry.name,
                    code: foundCountry.code
                });
            }
        }
    }, [billingInformation.countryId, countries, selectedBillingCountry]);

    // Set selected country when countries data and company billing info are available
    useEffect(() => {
        if (companyBillingInformation.countryId && countries && countries.length > 0 && !selectedCompanyCountry) {
            const foundCountry = countries.find((country: any) => country.id === companyBillingInformation.countryId);
            if (foundCountry) {
                setSelectedCompanyCountry({
                    id: foundCountry.id,
                    name: foundCountry.name,
                    code: foundCountry.code
                });
            }
        }
    }, [companyBillingInformation.countryId, countries, selectedCompanyCountry]);

    // Function to extract country code from VAT number
    const extractCountryCodeFromVAT = (vatNumber: string): string => {
        if (!vatNumber || vatNumber.length < 2) return "";
        const countryCode = vatNumber.substring(0, 2).toUpperCase();
        if (/^[A-Z]{2}$/.test(countryCode)) {
            return countryCode;
        }
        return "";
    };

    // Function to validate VAT number format
    const validateVATNumberFormat = (vatNumber: string): boolean => {
        if (!vatNumber) return true; // Optional field
        
        // Basic format validation - should start with 2 letters followed by numbers/letters
        const vatPattern = /^[A-Z]{2}[A-Z0-9]{2,12}$/;
        return vatPattern.test(vatNumber.toUpperCase());
    };

    // Function to validate VAT number with API
    const validateVATNumberWithAPI = async (vatNumber: string): Promise<boolean> => {
        if (!vatNumber) return true; // Optional field
        
        // First check format
        if (!validateVATNumberFormat(vatNumber)) {
            setVatValidationError(t("billingSettings.error.vatInvalidFormat"));
            setVatValidationStatus("invalid");
            return false;
        }

        // Extract country code from VAT number
        const countryCode = extractCountryCodeFromVAT(vatNumber);
        if (!countryCode) {
            setVatValidationError(t("billingSettings.error.vatInvalidCountryCode"));
            setVatValidationStatus("invalid");
            return false;
        }

        // Extract VAT number without country code
        const vatNumberWithoutCountryCode = vatNumber.substring(2);

        setIsValidatingVAT(true);
        setVatValidationStatus("pending");
        setVatValidationError("");

        try {
            const vatValidationResponse = await appDispatch(validateVATNumber({
                countryCode: countryCode,
                vatNumber: vatNumberWithoutCountryCode,
                companyId: company?.id
            })).unwrap();

            if (vatValidationResponse.valid) {
                setVatValidationStatus("valid");
                setVatValidationError("");
                GlobalService.showToast("success", t("billingSettings.vatValidation.title"), t("billingSettings.vatValidation.valid"));
                return true;
            } else {
                setVatValidationStatus("invalid");
                setVatValidationError(t("billingSettings.vatValidation.invalid"));
                return false;
            }
        } catch (error) {
            console.error("VAT validation error:", error);
            setVatValidationStatus("invalid");
            setVatValidationError(t("billingSettings.error.vatValidationFailed"));
            return false;
        } finally {
            setIsValidatingVAT(false);
        }
    };

    // Handle ID type change
    const handleIdTypeChange = (newIdType: string) => {
        setCompanyBillingInformation(prev => ({
            ...prev,
            idType: newIdType,
            // Clear validation error when changing ID type
        }));
        setVatValidationError("");
    };

    // Handle ID value change
    const handleIdValueChange = (value: string) => {
        setCompanyBillingInformation(prev => ({
            ...prev,
            vatNumber: value.toUpperCase(),
        }));
        // Clear validation status when user changes the value
        setVatValidationError("");
        setVatValidationStatus(null);
    };

    // Handle default address type change
    const handleDefaultAddressChange = async (type: "personal" | "company") => {
        setDefaultAddressType(type);
        
        // Update user profile immediately when default address selection changes
        if (!user?.id) return;
        
        setIsLoading(true);
        try {
            // Check if personal billing information is complete
            const hasPersonalBilling = isCompleteBillingAddress(billingInformation);
            
            // Set isDefault based on new selection - ensure mutual exclusivity
            const billingInfoWithDefault = {
                ...billingInformation,
                isDefault: hasPersonalBilling && type === "personal"
            };

            const companyBillingInfoWithDefault = {
                ...companyBillingInformation,
                isDefault: !hasPersonalBilling || type === "company"
            };

            // Update user profile with new default selection
            const updateData = {
                billingInformation: billingInfoWithDefault,
                companyBillingInformation: companyBillingInfoWithDefault,
                ...(user.photo && { photo: user.photo }),
                ...(user.cover && { cover: user.cover }),
                ...(user.firstName && { firstName: user.firstName }),
                ...(user.lastName && { lastName: user.lastName }),
                ...(user.bio && { bio: user.bio }),
                ...(user.title && { title: user.title }),
                ...(user.phone && { phone: user.phone }),
                ...(user.address && { address: user.address }),
                ...(user.hourlyRate && { hourlyRate: user.hourlyRate }),
                ...(user.socialLinks && { socialLinks: user.socialLinks }),
                ...(user.isPhoneVerified !== undefined && { isPhoneVerified: user.isPhoneVerified }),
                ...(user.cameraPermission !== undefined && { cameraPermission: user.cameraPermission }),
                ...(user.enableNotification !== undefined && { enableNotification: user.enableNotification })
            };
            
            await dispatch(UpdateUserProfile({
                id: user.id,
                data: updateData
            }) as any);
            
            GlobalService.showToast("success", t("common.success"), t("billingSettings.success.defaultBillingUpdated"));
        } catch (error) {
            console.error("Error updating default billing address:", error);
            GlobalService.showToast("error", t("common.error"), t("billingSettings.error.defaultBillingUpdateFailed"));
            // Revert the selection on error
            setDefaultAddressType(defaultAddressType);
        } finally {
            setIsLoading(false);
        }
    };

    // Validate personal billing information
    const validatePersonalBilling = (): boolean => {
        const errors: {[key: string]: string} = {};
        
        if (!billingInformation.street.trim()) {
            errors.street = t("billingSettings.validation.streetRequired");
        }
        if (!billingInformation.city.trim()) {
            errors.city = t("billingSettings.validation.cityRequired");
        }
        if (!billingInformation.state.trim()) {
            errors.state = t("billingSettings.validation.stateRequired");
        }
        if (!billingInformation.zipCode.trim()) {
            errors.zipCode = t("billingSettings.validation.zipCodeRequired");
        }
        if (!billingInformation.countryId) {
            errors.country = t("billingSettings.validation.countryRequired");
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Validate company billing information
    const validateCompanyBilling = (): boolean => {
        const errors: {[key: string]: string} = {};
        
        if (!companyBillingInformation.street.trim()) {
            errors.companyStreet = t("billingSettings.validation.companyStreetRequired");
        }
        if (!companyBillingInformation.city.trim()) {
            errors.companyCity = t("billingSettings.validation.companyCityRequired");
        }
        if (!companyBillingInformation.state.trim()) {
            errors.companyState = t("billingSettings.validation.companyStateRequired");
        }
        if (!companyBillingInformation.zipCode.trim()) {
            errors.companyZipCode = t("billingSettings.validation.companyZipCodeRequired");
        }
        if (!companyBillingInformation.countryId) {
            errors.companyCountry = t("billingSettings.validation.companyCountryRequired");
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveBillingInformation = async () => {
        if (!user?.id) return;
        
        // Validate personal billing information
        if (!validatePersonalBilling()) {
            return;
        }
        
        setIsLoading(true);
        try {
            // Set isDefault based on current selection - ensure mutual exclusivity
            const billingInfoWithDefault = {
                street: billingInformation.street || "",
                city: billingInformation.city || "",
                state: billingInformation.state || "",
                zipCode: billingInformation.zipCode || "",
                country: billingInformation.country || "",
                countryId: billingInformation.countryId || "",
                countryCode: billingInformation.countryCode || "",
                isDefault: defaultAddressType === "personal"
            };

            // Also update company billing info to ensure mutual exclusivity
            const companyBillingInfoWithDefault = {
                street: companyBillingInformation.street || "",
                city: companyBillingInformation.city || "",
                state: companyBillingInformation.state || "",
                zipCode: companyBillingInformation.zipCode || "",
                country: companyBillingInformation.country || "",
                countryId: companyBillingInformation.countryId || "",
                countryCode: companyBillingInformation.countryCode || "",
                companyName: companyBillingInformation.companyName || "",
                taxId: companyBillingInformation.taxId || "",
                vatNumber: companyBillingInformation.vatNumber || "",
                idType: companyBillingInformation.idType || "vatNumber",
                isDefault: defaultAddressType === "company",
                isVatValid: companyBillingInformation.isVatValid || false
            };

            // Include existing user data to preserve it during update
            const updateData = {
                billingInformation: billingInfoWithDefault,
                companyBillingInformation: companyBillingInfoWithDefault,
                ...(user.photo && { photo: user.photo }),
                ...(user.cover && { cover: user.cover }),
                ...(user.firstName && { firstName: user.firstName }),
                ...(user.lastName && { lastName: user.lastName }),
                ...(user.bio && { bio: user.bio }),
                ...(user.title && { title: user.title }),
                ...(user.phone && { phone: user.phone }),
                ...(user.address && { address: user.address }),
                ...(user.hourlyRate && { hourlyRate: user.hourlyRate }),
                ...(user.socialLinks && { socialLinks: user.socialLinks }),
                ...(user.isPhoneVerified !== undefined && { isPhoneVerified: user.isPhoneVerified }),
                ...(user.cameraPermission !== undefined && { cameraPermission: user.cameraPermission }),
                ...(user.enableNotification !== undefined && { enableNotification: user.enableNotification })
            };
            
            await dispatch(UpdateUserProfile({
                id: user.id,
                data: updateData
            }) as any);
            
            setIsEditingBilling(false);
            GlobalService.showToast("success", t("common.success"), t("billingSettings.success.billingUpdated"));
        } catch (error) {
            GlobalService.showToast("error", t("common.error"), t("billingSettings.error.billingUpdateFailed"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveCompanyBillingInformation = async () => {
        if (!user?.id) return;
        
        // Validate company billing information
        if (!validateCompanyBilling()) {
            return;
        }
        
        // Validate VAT number only for VAT Number VIES (EU) type
        let vatValidationResult = null;
        const idType = companyBillingInformation.idType || "vatNumber";
        
        if (idType === "vatNumberVies" && companyBillingInformation.vatNumber) {
            // Only validate VAT for VIES type
            vatValidationResult = await validateVATNumberWithAPI(companyBillingInformation.vatNumber);
            // Don't block save if VAT validation fails - just set isVatValid to false
        } else if (idType !== "vatNumberVies") {
            // For non-VIES types, set isVatValid to false
            vatValidationResult = false;
        }
        
        setIsLoading(true);
        try {
            // Check if personal billing information is complete
            const hasPersonalBilling = isCompleteBillingAddress(billingInformation);
            
            // Set isDefault based on current selection or default to company if no personal billing exists
            const companyBillingInfoWithDefault = {
                ...companyBillingInformation,
                isDefault: !hasPersonalBilling || defaultAddressType === "company",
                isVatValid: vatValidationResult === true // Include VAT validation result
            };

            // Also update personal billing info to ensure mutual exclusivity
            // Check if billingInformation exists, otherwise create default state
            const billingInfoWithDefault = {
                street: billingInformation.street || "",
                city: billingInformation.city || "",
                state: billingInformation.state || "",
                zipCode: billingInformation.zipCode || "",
                country: billingInformation.country || "",
                countryId: billingInformation.countryId || "",
                countryCode: billingInformation.countryCode || "",
                isDefault: hasPersonalBilling && defaultAddressType === "personal"
            };

            // Update user profile with both billing information objects
            const updateData = {
                billingInformation: billingInfoWithDefault,
                companyBillingInformation: companyBillingInfoWithDefault,
                ...(user.photo && { photo: user.photo }),
                ...(user.cover && { cover: user.cover }),
                ...(user.firstName && { firstName: user.firstName }),
                ...(user.lastName && { lastName: user.lastName }),
                ...(user.bio && { bio: user.bio }),
                ...(user.title && { title: user.title }),
                ...(user.phone && { phone: user.phone }),
                ...(user.address && { address: user.address }),
                ...(user.hourlyRate && { hourlyRate: user.hourlyRate }),
                ...(user.socialLinks && { socialLinks: user.socialLinks }),
                ...(user.isPhoneVerified !== undefined && { isPhoneVerified: user.isPhoneVerified }),
                ...(user.cameraPermission !== undefined && { cameraPermission: user.cameraPermission }),
                ...(user.enableNotification !== undefined && { enableNotification: user.enableNotification })
            };
            
            await dispatch(UpdateUserProfile({
                id: user.id,
                data: updateData
            }) as any);
            
            // If company exists, update it; otherwise create a new one
            if (company) {
                const companyUpdateData = {
                    vatNumber: companyBillingInformation.vatNumber || company.vatNumber,
                    taxId: companyBillingInformation.taxId || company.taxId,
                    address: companyBillingInformation.street || company.address,
                    city: companyBillingInformation.city || company.city,
                    state: companyBillingInformation.state || company.state,
                    postalCode: companyBillingInformation.zipCode || company.postalCode,
                    country: companyBillingInformation.countryId || company.country,
                    isVatValid: vatValidationResult === true, // Set based on validation result
                    // Preserve other company fields
                    name: company.name,
                    description: company.description,
                    industry: {id:Number(company.industry.id)},
                    companySize: {id:Number(company.companySize.id)},
                    year: company.year,
                    phone: company.phone,
                    email: company.email,
                    website: company.website,
                    legalStatus: {id:Number(company.legalStatus.id)},
                    registrationNumber: company.registrationNumber,
                    trading: company.trading,
                    minHourly: company.minHourly,
                    maxHourly: company.maxHourly,
                    userId: { id: user.id }
                };
                
                await appDispatch(updateUserCompanies({
                    id: company.id,
                    data: companyUpdateData
                }));
            } else {
                // Create new company if it doesn't exist
                const companyCreateData = {
                    name: companyBillingInformation.companyName || t("billingSettings.companyName"),
                    description: t("billingSettings.companyCreatedFromBilling"),
                    vatNumber: companyBillingInformation.vatNumber || "",
                    taxId: companyBillingInformation.taxId || "",
                    address: companyBillingInformation.street || "",
                    city: companyBillingInformation.city || "",
                    state: companyBillingInformation.state || "",
                    postalCode: companyBillingInformation.zipCode || "",
                    country: companyBillingInformation.countryId || "",
                    isVatValid: vatValidationResult === true, // Set based on validation result
                    // Set default values for required fields
                    industry: { id: 1 }, // Default industry
                    companySize: { id: 1 }, // Default company size
                    year: new Date().getFullYear(),
                    phone: user.phone || "",
                    email: user.email || "",
                    website: "",
                    legalStatus: { id: 1 }, // Default legal status
                    registrationNumber: "",
                    trading: "",
                    userId: { id: user.id }
                };
                
                await appDispatch(createUserCompany({
                    data: companyCreateData
                }));
            }
            
            setIsEditingCompanyBilling(false);
            GlobalService.showToast("success", t("common.success"), t("billingSettings.success.companyBillingUpdated"));
        } catch (error) {
            console.error("Error updating company billing information:", error);
            GlobalService.showToast("error", t("common.error"), t("billingSettings.error.companyBillingUpdateFailed"));
        } finally {
            setIsLoading(false);
        }
    };

    // Use the comprehensive validation function from billingUtils
    const hasBillingAddress = isCompleteBillingAddress(billingInformation);
    const hasCompanyBillingAddress = isCompleteCompanyBillingAddress(companyBillingInformation);
    return <>
        <Card className="p-6" style={{ textAlign: "left" }}>
            <h2 className="text-xl font-semibold mb-6">{t("billingSettings.title")}</h2>

            {/* Default Address Selection */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="text-lg font-medium mb-3">{t("billingAddressDialog.defaultBillingAddress")}</h3>
                <p className="text-sm text-gray-600 mb-4">{t("billingAddressDialog.chooseDefaultAddress")}</p>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="radio"
                                name="default-address"
                                checked={defaultAddressType === "personal"}
                                onChange={() => handleDefaultAddressChange("personal")}
                                disabled={isLoading}
                            />
                            <span>{t("billingAddressDialog.usePersonalAsDefault")}</span>
                            {isLoading && defaultAddressType === "personal" && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            )}
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="radio"
                                name="default-address"
                                checked={defaultAddressType === "company"}
                                onChange={() => handleDefaultAddressChange("company")}
                                disabled={isLoading}
                            />
                            <span>{t("billingAddressDialog.useCompanyAsDefault")}</span>
                            {isLoading && defaultAddressType === "company" && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            )}
                        </label>
                    </div>
            </div>

            <div className="space-y-6">
                {/* Personal Billing Address */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <User className="h-5 w-5 text-blue-600" />
                            <h3 className="font-medium">{t("billingAddressDialog.personalBillingAddress")}</h3>
                            {defaultAddressType === "personal" && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                    {t("billingAddressDialog.default")}
                                </span>
                            )}
                        </div>
                        {!isEditingBilling && (
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setIsEditingBilling(true)}
                                className="flex items-center gap-2"
                            >
                                <Edit3 className="h-4 w-4" />
                                {hasBillingAddress ? t("billingSettings.editBilling") : t("billingSettings.addBilling")} Address
                            </Button>
                        )}
                    </div>

                    {isEditingBilling ? (
                        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="street">{t("billingSettings.streetAddress")} *</Label>
                                    <Input
                                        id="street"
                                        value={billingInformation.street}
                                        onChange={(e) => setBillingInformation(prev => ({ ...prev, street: e.target.value }))}
                                        placeholder={t("billingSettings.streetAddress")}
                                    />
                                    {validationErrors.street && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.street}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="city">{t("billingSettings.city")} *</Label>
                                    <Input
                                        id="city"
                                        value={billingInformation.city}
                                        onChange={(e) => setBillingInformation(prev => ({ ...prev, city: e.target.value }))}
                                        placeholder={t("billingSettings.city")}
                                    />
                                    {validationErrors.city && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.city}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="state">{t("billingSettings.stateProvince")} *</Label>
                                    <Input
                                        id="state"
                                        value={billingInformation.state}
                                        onChange={(e) => setBillingInformation(prev => ({ ...prev, state: e.target.value }))}
                                        placeholder={t("billingSettings.stateProvince")}
                                    />
                                    {validationErrors.state && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.state}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="zipCode">{t("billingSettings.zipPostalCode")} *</Label>
                                    <Input
                                        id="zipCode"
                                        value={billingInformation.zipCode}
                                        onChange={(e) => setBillingInformation(prev => ({ ...prev, zipCode: e.target.value }))}
                                        placeholder={t("billingSettings.zipPostalCode")}
                                    />
                                    {validationErrors.zipCode && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.zipCode}
                                        </div>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <Label htmlFor="country">{t("billingSettings.country")} *</Label>
                                    <CountryDropdown
                                        selectedCountry={selectedBillingCountry}
                                        onCountrySelect={(country) => {
                                            setSelectedBillingCountry(country);
                                            setBillingInformation(prev => ({
                                                ...prev,
                                                countryId: country?.id || "",      // Save ID for reference
                                                country: country?.name || "",      // Save name for display
                                                countryCode: country?.code || "",  // Save code (initial) for tax
                                            }));
                                        }}
                                        placeholder={t("billingSettings.country")}
                                    />
                                    {validationErrors.country && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.country}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setIsEditingBilling(false)}
                                    className="flex items-center gap-2"
                                >
                                    <X className="h-4 w-4" />
                                    {t("billingSettings.cancel")}
                                </Button>
                                <Button 
                                    size="sm" 
                                    onClick={handleSaveBillingInformation}
                                    disabled={isLoading}
                                    className="flex items-center gap-2"
                                >
                                    <Save className="h-4 w-4" />
                                    {isLoading ? t("billingSettings.saving") : t("billingSettings.saveChanges")}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gray-50 p-4 rounded-lg">
                            {hasBillingAddress ? (
                                <div className="space-y-1">
                                    {billingInformation.street && <div>{billingInformation.street}</div>}
                                    <div>
                                        {billingInformation.city}
                                        {billingInformation.state && `, ${billingInformation.state}`}
                                        {billingInformation.zipCode && ` ${billingInformation.zipCode}`}
                                    </div>
                                    {billingInformation.country && <div>{billingInformation.country}</div>}
                                </div>
                            ) : (
                                <div className="text-gray-500 text-sm">{t("billingAddressDialog.noBillingAddress")}</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Company Billing Address */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-green-600" />
                            <h3 className="font-medium">{t("billingAddressDialog.companyBillingAddress")}</h3>
                            {defaultAddressType === "company" && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                    {t("billingAddressDialog.default")}
                                </span>
                            )}
                        </div>
                        {!isEditingCompanyBilling && (
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setIsEditingCompanyBilling(true)}
                                className="flex items-center gap-2"
                            >
                                <Edit3 className="h-4 w-4" />
                                {hasCompanyBillingAddress ? t("billingSettings.editCompanyBilling") : t("billingSettings.addCompanyBilling")} Company Address
                            </Button>
                        )}
                    </div>

                    {isEditingCompanyBilling ? (
                        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <Label htmlFor="companyName">{t("billingSettings.companyName")}</Label>
                                    <Input
                                        id="companyName"
                                        value={companyBillingInformation.companyName}
                                        onChange={(e) => setCompanyBillingInformation(prev => ({ ...prev, companyName: e.target.value }))}
                                        placeholder={t("billingSettings.companyName")}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label>{t("billingSettings.selectIdType")}</Label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="radio"
                                                name="company-id-type"
                                                checked={companyBillingInformation.idType === "taxId"}
                                                onChange={() => handleIdTypeChange("taxId")}
                                            />
                                            <span>{t("billingSettings.taxId")}</span>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="radio"
                                                name="company-id-type"
                                                checked={companyBillingInformation.idType === "registrationNumber"}
                                                onChange={() => handleIdTypeChange("registrationNumber")}
                                            />
                                            <span>{t("billingSettings.businessNumber")}</span>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="radio"
                                                name="company-id-type"
                                                checked={companyBillingInformation.idType === "vatNumber"}
                                                onChange={() => handleIdTypeChange("vatNumber")}
                                            />
                                            <span>{t("billingSettings.vatNumberEU")}</span>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="radio"
                                                name="company-id-type"
                                                checked={companyBillingInformation.idType === "vatNumberVies"}
                                                onChange={() => handleIdTypeChange("vatNumberVies")}
                                            />
                                            <span>{t("billingSettings.vatNumberVIES")}</span>
                                        </label>
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <Label htmlFor="idValue">{idConfig.label}</Label>
                                    <Input
                                        id="idValue"
                                        value={companyBillingInformation.vatNumber}
                                        onChange={(e) => handleIdValueChange(e.target.value)}
                                        placeholder={idConfig.placeholder}
                                    />
                                    {vatValidationError && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {vatValidationError}
                                        </div>
                                    )}
                                    {vatValidationStatus === "valid" && (
                                        <div className="flex items-center text-green-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {t("billingSettings.vatValidation.valid")}
                                        </div>
                                    )}
                                    {companyBillingInformation.idType === "vatNumberVies" && company?.isVatValid && !vatValidationStatus && (
                                        <div className="flex items-center text-green-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {t("billingSettings.vatValidation.valid")}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="companyStreet">{t("billingSettings.streetAddress")} *</Label>
                                    <Input
                                        id="companyStreet"
                                        value={companyBillingInformation.street}
                                        onChange={(e) => setCompanyBillingInformation(prev => ({ ...prev, street: e.target.value }))}
                                        placeholder={t("billingSettings.streetAddress")}
                                    />
                                    {validationErrors.companyStreet && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.companyStreet}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="companyCity">{t("billingSettings.city")} *</Label>
                                    <Input
                                        id="companyCity"
                                        value={companyBillingInformation.city}
                                        onChange={(e) => setCompanyBillingInformation(prev => ({ ...prev, city: e.target.value }))}
                                        placeholder={t("billingSettings.city")}
                                    />
                                    {validationErrors.companyCity && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.companyCity}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="companyState">{t("billingSettings.stateProvince")} *</Label>
                                    <Input
                                        id="companyState"
                                        value={companyBillingInformation.state}
                                        onChange={(e) => setCompanyBillingInformation(prev => ({ ...prev, state: e.target.value }))}
                                        placeholder={t("billingSettings.stateProvince")}
                                    />
                                    {validationErrors.companyState && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.companyState}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="companyZipCode">{t("billingSettings.zipPostalCode")} *</Label>
                                    <Input
                                        id="companyZipCode"
                                        value={companyBillingInformation.zipCode}
                                        onChange={(e) => setCompanyBillingInformation(prev => ({ ...prev, zipCode: e.target.value }))}
                                        placeholder={t("billingSettings.zipPostalCode")}
                                    />
                                    {validationErrors.companyZipCode && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.companyZipCode}
                                        </div>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <Label htmlFor="companyCountry">{t("billingSettings.country")} *</Label>
                                    <CountryDropdown
                                        selectedCountry={selectedCompanyCountry}
                                        onCountrySelect={(country) => {
                                            setSelectedCompanyCountry(country);
                                            setCompanyBillingInformation(prev => ({
                                                ...prev,
                                                countryId: country?.id || "",      // Save ID for reference
                                                country: country?.name || "",      // Save name for display
                                                countryCode: country?.code || "",  // Save code (initial) for tax
                                            }));
                                        }}
                                        placeholder={t("billingSettings.country")}
                                        placement="top"
                                    />
                                    {validationErrors.companyCountry && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {validationErrors.companyCountry}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setIsEditingCompanyBilling(false)}
                                    className="flex items-center gap-2"
                                >
                                    <X className="h-4 w-4" />
                                    {t("billingSettings.cancel")}
                                </Button>
                                <Button 
                                    size="sm" 
                                    onClick={handleSaveCompanyBillingInformation}
                                    disabled={isLoading}
                                    className="flex items-center gap-2"
                                >
                                    <Save className="h-4 w-4" />
                                    {isLoading ? t("billingSettings.saving") : t("billingSettings.saveChanges")}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gray-50 p-4 rounded-lg">
                            {hasCompanyBillingAddress ? (
                                <div className="space-y-2">
                                    {companyBillingInformation.companyName && (
                                        <div className="font-medium">{companyBillingInformation.companyName}</div>
                                    )}
                                    {companyBillingInformation.vatNumber && (
                                        <div className="text-sm text-gray-600">
                                            {idConfig.label}: {companyBillingInformation.vatNumber}
                                        </div>
                                    )}
                                    <div className="space-y-1">
                                        {companyBillingInformation.street && <div>{companyBillingInformation.street}</div>}
                                        <div>
                                            {companyBillingInformation.city}
                                            {companyBillingInformation.state && `, ${companyBillingInformation.state}`}
                                            {companyBillingInformation.zipCode && ` ${companyBillingInformation.zipCode}`}
                                        </div>
                                        {companyBillingInformation.country && <div>{companyBillingInformation.country}</div>}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-gray-500 text-sm">{t("billingAddressDialog.noCompanyBillingAddress")}</div>
                            )}
                        </div>
                    )}
                </div>

               
            </div>
        </Card>
    </>;
};

export default BillingComponent;